/**
 * @fileoverview The shifts of a letterbox plan, one row per meeting point.
 *
 * A shift is ten BAs meeting at one address and time, with standby BAs booked
 * on top so a no show can be covered on the spot. This is where the city
 * manager sets how many standbys a shift carries, hands a mission over to one
 * when a BA does not show, and sends the unused ones home on half pay.
 *
 * The data is the plan's own missions grouped by meeting point, so nothing
 * here needs the missions table to be loaded.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { readGraphQLData } from 'Components/PowerBanks/hooks'
import SubmitButton from 'Components/Form/SubmitButton'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import { SnackBarContext } from 'Components/SnackBar'
import {
  bookShiftForUser,
  coverMissionWithStandby,
  duplicateCityPlanDay,
  fetchCampaignStandbySettings,
  fetchCityAmbassadors,
  fetchMissionCluster,
  fetchMissionClusters,
  releaseStandby,
  setStandbySlots,
  setStandbySlotsForPlan,
} from 'Module/graphQL'
import moment from 'moment'
import { PATHS } from 'Config/Routing/paths'

const Wrapper = styled.div`
  .shift {
    border: 1px solid #eceef0;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  .shift-head {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 14px;
    cursor: pointer;
  }
  .shift-when {
    width: 170px;
    font-weight: 600;
  }
  .shift-where {
    flex-grow: 1;
  }
  .counts {
    display: flex;
    gap: 14px;
    font-size: 13px;
    color: #5a6b82;
  }
  .counts strong {
    color: #011533;
  }
  .warn {
    color: #c0392b;
    font-weight: 600;
  }
  .shift-body {
    border-top: 1px solid #eceef0;
    padding: 12px 14px;
  }
  .slots {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .slots input {
    width: 64px;
    padding: 4px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  th,
  td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #f2f4f7;
  }
  .pill {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    color: #ffffff;
  }
  .pill.open {
    background: #1878cf;
  }
  .pill.covered {
    background: #2e7d32;
  }
  .pill.released {
    background: #7e8fa5;
  }
  .empty {
    color: #5a6b82;
    padding: 8px 0;
  }
  .plan-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;
    margin-bottom: 12px;
    border: 1px solid #eceef0;
    border-radius: 4px;
    background: #f8f9fb;
  }
  .plan-bar input {
    width: 64px;
    padding: 4px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
  }
  .plan-bar .hint {
    color: #5a6b82;
    font-size: 13px;
  }
  .book-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
  .book-row select {
    padding: 4px 8px;
    border: 1px solid #d9dee6;
    border-radius: 4px;
    max-width: 260px;
  }
`

/** "Mon 03/11, 08:15" from a meeting point's local date time. */
export const formatShiftWhen = dateTime => {
  if (!dateTime) return 'No time'
  const when = moment(dateTime)
  return when.isValid() ? when.format('ddd DD/MM, HH:mm') : String(dateTime)
}

/** A shift needs attention when someone did not show and a sub is waiting. */
export const needsAttention = cluster =>
  cluster.noShowCount > 0 && cluster.standbyBookedCount > 0

const outcomePill = mission => {
  if (mission.standbyOutcome === 'COVERED')
    return <span className="pill covered">Covered</span>
  if (mission.standbyOutcome === 'RELEASED')
    return <span className="pill released">Sent home</span>
  return <span className="pill open">Standby</span>
}

const ShiftRow = ({
  cityPlanId,
  cluster,
  canManage,
  ambassadors,
  ambassadorsState,
  onChanged,
}) => {
  const runSnackBar = useContext(SnackBarContext)
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [busy, setBusy] = useState(false)
  const [slots, setSlots] = useState(cluster.standbyCount)
  const [bookFor, setBookFor] = useState('')

  const meetingPoint = cluster.meetingPoint || {}

  const load = useCallback(async () => {
    setBusy(true)
    try {
      const data = await readGraphQLData(
        await fetchMissionCluster({ cityPlanId, meetingPoint }),
      )
      setDetail(data.missionCluster)
    } catch (error) {
      runSnackBar({ type: 'error', msg: 'Could not load this shift.' })
    } finally {
      setBusy(false)
    }
  }, [cityPlanId, meetingPoint.address, meetingPoint.dateTime]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && !detail) load()
  }, [open, detail, load])

  const run = async (action, message) => {
    setBusy(true)
    try {
      await action()
      runSnackBar({ type: 'success', msg: message })
      setDetail(null)
      await load()
      if (onChanged) onChanged()
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'That did not work.',
      })
      setBusy(false)
    }
  }

  const onSaveSlots = () =>
    run(
      () => setStandbySlots({ cityPlanId, meetingPoint, count: Number(slots) }),
      `Standby slots set to ${Number(slots)}.`,
    )

  const onCover = (standbyMissionId, missionId) =>
    run(
      () =>
        coverMissionWithStandby({ cityPlanId, standbyMissionId, missionId }),
      'The standby took the mission over.',
    )

  const onBookShift = () =>
    run(
      () => bookShiftForUser({ cityPlanId, meetingPoint, userId: bookFor }),
      'The whole shift is booked for that BA.',
    )

  const onRelease = standbyMissionId =>
    run(
      () => releaseStandby({ cityPlanId, standbyMissionId }),
      'Standby sent home on half pay.',
    )

  const missingMissions = ((detail && detail.missions) || []).filter(
    mission => mission.hasBoundary && !mission.checkInTime,
  )

  return (
    <div className="shift">
      <div className="shift-head" onClick={() => setOpen(value => !value)}>
        <span className="shift-when">
          {formatShiftWhen(meetingPoint.dateTime)}
        </span>
        <span className="shift-where">{meetingPoint.address}</span>
        <span className="counts">
          <span>
            <strong>{cluster.checkedInCount}</strong>/{cluster.missionCount} in
          </span>
          <span>
            subs <strong>{cluster.standbyBookedCount}</strong>/
            {cluster.standbyCount}
          </span>
          {cluster.noShowCount > 0 && (
            <span className={needsAttention(cluster) ? 'warn' : ''}>
              {cluster.noShowCount} no show
            </span>
          )}
          {cluster.coveredCount > 0 && (
            <span>{cluster.coveredCount} covered</span>
          )}
          {cluster.releasedCount > 0 && (
            <span>{cluster.releasedCount} sent home</span>
          )}
        </span>
      </div>

      {open && (
        <div className="shift-body">
          {busy && <LoadingIndicator />}

          {canManage && (
            <div className="slots">
              <span>Standby BAs for this shift</span>
              <input
                type="number"
                min="0"
                max="10"
                value={slots}
                onChange={event => setSlots(event.target.value)}
              />
              <SubmitButton
                value="Save"
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={onSaveSlots}
              />
            </div>
          )}

          {canManage && (
            <div className="book-row">
              <span>Book this whole shift for</span>
              <select
                value={bookFor}
                onChange={event => setBookFor(event.target.value)}
              >
                <option value="">
                  {ambassadorsState === 'loading'
                    ? 'Loading BAs...'
                    : ambassadorsState === 'failed'
                    ? 'Could not load the BAs, reload the page'
                    : ambassadors.length
                    ? 'Pick a BA'
                    : 'No active BAs in this city'}
                </option>
                {ambassadors.map(ba => (
                  <option key={ba.id} value={ba.id}>
                    {ba.firstName} {ba.lastName}
                  </option>
                ))}
              </select>
              <SubmitButton
                value="Book shift"
                size="small"
                variant="outlined"
                disabled={busy || !bookFor}
                onClick={onBookShift}
              />
              <span className="empty">
                Books the missions and the standby seats for that BA only.
                Nobody else ever sees them.
              </span>
            </div>
          )}

          <table>
            <thead>
              <tr>
                <th>Standby</th>
                <th>BA</th>
                <th>Checked in</th>
                <th>State</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {((detail && detail.standby) || []).map(standby => (
                <tr key={standby.id}>
                  <td>{standby.label}</td>
                  <td>
                    {standby.userId ? `#${standby.userId}` : 'Not booked'}
                  </td>
                  <td>{standby.checkInTime ? 'Yes' : 'No'}</td>
                  <td>{outcomePill(standby)}</td>
                  {canManage && (
                    <td>
                      {!standby.standbyOutcome && standby.checkInTime && (
                        <>
                          {missingMissions.map(mission => (
                            <SubmitButton
                              key={mission.id}
                              value={`Cover ${mission.label}`}
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => onCover(standby.id, mission.id)}
                            />
                          ))}
                          <SubmitButton
                            value="Send home, half pay"
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => onRelease(standby.id)}
                          />
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {detail && !detail.standby.length && (
                <tr>
                  <td colSpan={canManage ? 5 : 4} className="empty">
                    No standby BAs on this shift yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

ShiftRow.propTypes = {
  cityPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  cluster: PropTypes.object.isRequired,
  canManage: PropTypes.bool,
  ambassadors: PropTypes.array,
  ambassadorsState: PropTypes.oneOf(['loading', 'ready', 'failed']),
  onChanged: PropTypes.func,
}

ShiftRow.defaultProps = {
  ambassadors: [],
  ambassadorsState: 'loading',
}

const ShiftsPanel = ({ cityPlanId, cityId, campaignId, canManage }) => {
  const runSnackBar = useContext(SnackBarContext)
  const [clusters, setClusters] = useState(null)
  const [ambassadors, setAmbassadors] = useState([])
  const [ambassadorsState, setAmbassadorsState] = useState('loading')
  const [campaignPercent, setCampaignPercent] = useState(undefined)
  const [applying, setApplying] = useState(false)
  const [copyFrom, setCopyFrom] = useState('')
  const [copyTo, setCopyTo] = useState('')
  const [copying, setCopying] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await readGraphQLData(
        await fetchMissionClusters({ cityPlanId }),
      )
      setClusters(data.missionClusters || [])
    } catch (error) {
      setClusters([])
      runSnackBar({ type: 'error', msg: 'Could not load the shifts.' })
    }
  }, [cityPlanId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!cityId || !canManage) return
    const loadAmbassadors = async () => {
      try {
        const data = await readGraphQLData(
          await fetchCityAmbassadors({ cityId }),
        )
        const connection =
          data.city && data.city.brandAmbassadors
            ? data.city.brandAmbassadors
            : null
        const edges = (connection && connection.edges) || []
        setAmbassadors(edges.map(edge => edge.node).filter(Boolean))
        setAmbassadorsState('ready')
      } catch (error) {
        setAmbassadors([])
        setAmbassadorsState('failed')
      }
    }
    loadAmbassadors()
  }, [cityId, canManage])

  // The standby share is a campaign setting now; this page only applies it.
  useEffect(() => {
    if (!campaignId || !canManage) return
    const loadSettings = async () => {
      try {
        const data = await readGraphQLData(
          await fetchCampaignStandbySettings({ campaignId }),
        )
        const settings = data.campaignStandbySettings
        setCampaignPercent(settings ? settings.standbyPercent : null)
      } catch (error) {
        setCampaignPercent(null)
      }
    }
    loadSettings()
  }, [campaignId, canManage])

  const campaignSettingsLink = campaignId
    ? `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${campaignId}${
        PATHS.campaign.edit
      }`
    : null

  const onApplyToPlan = async () => {
    setApplying(true)
    try {
      const data = await readGraphQLData(
        await setStandbySlotsForPlan({
          cityPlanId,
          percent: Number(campaignPercent),
        }),
      )
      const result = data.setStandbySlotsForPlan
      runSnackBar({
        type: 'success',
        msg: `${
          result.shifts
        } shifts now carry ${campaignPercent}% standby. Added ${
          result.created
        }, removed ${result.removed}.`,
      })
      await load()
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not set the standby slots.',
      })
    } finally {
      setApplying(false)
    }
  }

  const onCopyDay = async () => {
    setCopying(true)
    try {
      const data = await readGraphQLData(
        await duplicateCityPlanDay({
          cityPlanId,
          fromDay: copyFrom,
          toDay: copyTo,
        }),
      )
      const result = data.duplicateCityPlanDay
      runSnackBar({
        type: 'success',
        msg: `Copied ${result.copied} missions from ${result.fromDay} to ${
          result.toDay
        } as drafts. Apply the standby share and publish when ready.`,
      })
      setCopyFrom('')
      setCopyTo('')
      await load()
    } catch (error) {
      runSnackBar({
        type: 'error',
        msg: (error && error.message) || 'Could not copy that day.',
      })
    } finally {
      setCopying(false)
    }
  }

  if (clusters === null) return <LoadingIndicator />

  const overbooked = campaignPercent !== null && campaignPercent !== undefined

  return (
    <Wrapper>
      {canManage && (
        <div className="plan-bar">
          {campaignPercent === undefined ? (
            <span className="hint">Loading the overbooking setting...</span>
          ) : overbooked ? (
            <>
              <span>
                Overbooking: <strong>{campaignPercent}%</strong> standby BAs per
                shift, from the campaign settings
              </span>
              <SubmitButton
                value={`Apply to all ${clusters.length} shifts`}
                size="small"
                variant="outlined"
                disabled={applying || !clusters.length}
                onClick={onApplyToPlan}
              />
            </>
          ) : (
            <span>Overbooking is off for this campaign.</span>
          )}
          {campaignSettingsLink && (
            <a className="hint" href={campaignSettingsLink}>
              Change it in the campaign settings
            </a>
          )}
        </div>
      )}

      {canManage && (
        <div className="plan-bar">
          <span>Copy a day</span>
          <input
            id="copy-day-from"
            type="date"
            aria-label="Day to copy"
            value={copyFrom}
            onChange={event => setCopyFrom(event.target.value)}
          />
          <span>to</span>
          <input
            id="copy-day-to"
            type="date"
            aria-label="Day to copy it to"
            value={copyTo}
            onChange={event => setCopyTo(event.target.value)}
          />
          <SubmitButton
            value="Copy day"
            size="small"
            variant="outlined"
            disabled={copying || !copyFrom || !copyTo}
            onClick={onCopyDay}
          />
          <span className="hint">
            Every mission of that day, with its polygons, labels and meeting
            points, copied as drafts to an empty day. Nothing is published.
          </span>
        </div>
      )}

      {!clusters.length ? (
        <p className="empty">
          No shifts yet. A shift appears once its missions have a meeting point.
        </p>
      ) : (
        clusters.map(cluster => (
          <ShiftRow
            key={`${cluster.meetingPoint.address}@@${
              cluster.meetingPoint.dateTime
            }`}
            cityPlanId={cityPlanId}
            cluster={cluster}
            canManage={canManage}
            ambassadors={ambassadors}
            ambassadorsState={ambassadorsState}
            onChanged={load}
          />
        ))
      )}
    </Wrapper>
  )
}

ShiftsPanel.propTypes = {
  cityPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  cityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canManage: PropTypes.bool,
}

ShiftsPanel.defaultProps = {
  cityId: null,
  campaignId: null,
  canManage: false,
}

export default ShiftsPanel
